<template>
    <BaseForm data-spec-class="password-edit-form">
        <BaseAlertErrorServer
            v-if="hasServerErrors"
            :size="Size.Small"
            class="mb-4"
        />
        <div class="grid grid-cols-6 gap-6">
            <div class="col-span-6">
                <BaseFieldInput
                    v-model="fields.password"
                    :vuelidate-field="v$.password"
                    :error-messages="validationError?.password"
                    :type="TextFieldType.Password"
                    :label="t('forms.updateUserPassword.password.label')"
                    :placeholder="t('forms.updateUserPassword.password.placeholder')"
                    name="password"
                />
            </div>

            <div class="col-span-6">
                <BaseFieldInput
                    v-model="fields.password_confirmation"
                    :vuelidate-field="v$.password_confirmation"
                    :error-messages="validationError?.password_confirmation"
                    :type="TextFieldType.Password"
                    :label="t('forms.updateUserPassword.password_confirmation.label')"
                    :placeholder="t('forms.updateUserPassword.password_confirmation.placeholder')"
                    name="password_confirmation"
                />
            </div>
        </div>

        <template #submit>
            <BaseButton
                :is-loading="isUpdating"
                :is-disabled="v$.$errors.length > 0"
                data-spec-class="password-edit-form__submit"
                @click.prevent="handleSubmit"
            >
                {{ t('actions.save') }}
            </BaseButton>
        </template>
    </BaseForm>
</template>

<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { defineProps, defineEmits, reactive, computed, ref, withDefaults } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    required,
    sameAs,
    minLength as minLengthValidator,
} from '@/modules/your/i18n/i18n-validators';
import { Size, TextFieldType } from '@/types';
import { useToast } from '@/modules/your/composables/toast';
import BaseAlertErrorServer from './BaseAlertErrorServer.vue';
import BaseFieldInput from './BaseFieldInput.vue';
import BaseButton from './BaseButton.vue';
import BaseForm from './BaseForm.vue';

const { t } = useI18n();
const { addSuccessToast } = useToast();

interface PasswordFields {
    password: string;
    password_confirmation: string;
}

interface Props {
    itemId: string | number;
    updatePassword: (id: string | number, fields: PasswordFields) => Promise<void>;
    validationError?: Record<string, string[]> | null;
    isUpdating?: boolean;
    minLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
    isUpdating: false,
    minLength: 8,
    validationError: null,
});

const emits = defineEmits(['success']);

const hasServerErrors = ref(false);

const fields = reactive({
    password: '',
    password_confirmation: '',
});

const rules = computed(() => ({
    password: {
        required,
        minLength: minLengthValidator(props.minLength),
    },
    password_confirmation: {
        required,
        sameAs: sameAs(fields.password, t('forms.updateUserPassword.password.label')),
    },
}));

const v$ = useVuelidate(rules, fields);

const onReset = () => {
    v$.value.$reset();
    fields.password = '';
    fields.password_confirmation = '';
};

const handleSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) return;

    try {
        await props.updatePassword(props.itemId, fields);
        if (props.validationError) {
            return;
        }

        onReset();
        hasServerErrors.value = false;
        addSuccessToast(t('messages.success.saved'));
        emits('success');
    } catch {
        hasServerErrors.value = true;
    }
};
</script>
